<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import RichTextEditor from './RichTextEditor.svelte';
  import { appStore } from '../stores/appStore';
  import { AppApiClient } from '../api_contracts/app.client';
  import { UserProfile } from '../api_contracts/app';

  const dispatch = createEventDispatcher();

  // Props
	let { repositoryName, ownerId }: { repositoryName: string; ownerId: string } = $props();

  // State
  let client: AppApiClient | null = $state(null);
  let token: string = $state('');
  let profile: UserProfile | null = $state(null);
  let title = $state('');
  let description = $state('');
  let loading = $state(false);
  let error = $state('');

  // State for branches
  let branches: string[] = $state([]);
  let baseBranch = $state('main');
  let compareBranch = $state('');
  let loadingBranches = $state(true);

  // Members data
  let members: any[] = $state([]);
  let loadingMembers = $state(true);

  // Subscribe to store
  appStore.subscribe((state) => {
    client = state.client;
    token = state.token;
    profile = state.profile;
  });

  // Fetch branches from API
  async function fetchBranches() {
    if (!client || !token || !repositoryName || !ownerId) {
      return;
    }

    try {
      loadingBranches = true;

      const result = await client.getBranches({
        token,
        ownerId,
        repositoryName,
      });

      if (result.response.successFull) {
        branches = (result.response.branches || []).map((b: any) => b.name);

        // Set default branches
        const defaultBranch = result.response.defaultBranch || 'main';
        baseBranch = defaultBranch;

        // Set compare branch to first non-default branch
        const nonDefaultBranches = branches.filter(b => b !== defaultBranch);
        if (nonDefaultBranches.length > 0) {
          compareBranch = nonDefaultBranches[0];
        }
      }
    } catch (e) {
      console.error('Error fetching branches:', e);
    } finally {
      loadingBranches = false;
    }
  }

  // Fetch members from API
  async function fetchMembers() {
    if (!client || !token) return;

    try {
      loadingMembers = true;

      const result = await client.getRepositoryMembers({
        token,
        ownerId,
        repositoryName,
      });

      if (result.response.successFull) {
        members = result.response.members || [];
      }
    } catch (e) {
      console.error('Error fetching members:', e);
    } finally {
      loadingMembers = false;
    }
  }

  onMount(() => {
    fetchBranches();
    fetchMembers();
  });

  const labels = [
    { name: 'bug', color: '#d73a4a', description: "Something isn't working" },
    { name: 'documentation', color: '#0075ca', description: 'Improvements or additions to documentation' },
    { name: 'duplicate', color: '#cfd3d7', description: 'This issue or pull request already exists' },
    { name: 'enhancement', color: '#a2eeef', description: 'New feature or request' },
    { name: 'good first issue', color: '#7057ff', description: 'Good for newcomers' },
    { name: 'help wanted', color: '#008672', description: 'Extra attention is needed' },
    { name: 'invalid', color: '#e4e669', description: "This doesn't seem right" },
    { name: 'question', color: '#d876e3', description: 'Further information is requested' },
    { name: 'wontfix', color: '#ffffff', description: 'This will not be worked on' }
  ];

  const milestones = [
    { name: 'v1.0.0', progress: 0 },
    { name: 'v1.1.0', progress: 0 }
  ];

  let selectedReviewers: string[] = $state([]); // User IDs
  let selectedAssignees: string[] = $state([]); // User IDs
  let selectedLabels: string[] = $state([]);
  let selectedMilestone: string | null = $state(null);

  // Sidebar dropdown states
  let showReviewers = $state(false);
  let showAssignees = $state(false);
  let showLabels = $state(false);
  let showMilestones = $state(false);

  // Branch dropdown states
  let showBaseBranchDropdown = $state(false);
  let showCompareBranchDropdown = $state(false);

  function toggleReviewer(userId: string) {
    if (selectedReviewers.includes(userId)) {
        selectedReviewers = selectedReviewers.filter(id => id !== userId);
    } else {
        selectedReviewers = [...selectedReviewers, userId];
    }
  }

  function toggleAssignee(userId: string) {
    if (selectedAssignees.includes(userId)) {
        selectedAssignees = selectedAssignees.filter(id => id !== userId);
    } else {
        selectedAssignees = [...selectedAssignees, userId];
    }
  }

  function assignYourself() {
    if (profile && profile.id) {
      toggleAssignee(profile.id);
      showAssignees = false;
    }
  }

  function toggleLabel(name: string) {
    if (selectedLabels.includes(name)) {
        selectedLabels = selectedLabels.filter(n => n !== name);
    } else {
        selectedLabels = [...selectedLabels, name];
    }
  }

  function selectMilestone(name: string) {
      if (selectedMilestone === name) {
          selectedMilestone = null;
      } else {
          selectedMilestone = name;
      }
  }

  function selectBaseBranch(branchName: string) {
      baseBranch = branchName;
      showBaseBranchDropdown = false;
  }

  function selectCompareBranch(branchName: string) {
      compareBranch = branchName;
      showCompareBranchDropdown = false;
  }

  function cancel() {
      dispatch('cancel');
  }

  async function submit() {
      if (!client || !token || !repositoryName || !ownerId) {
          error = 'Missing required data';
          return;
      }

      if (!title.trim()) {
          error = 'Title is required';
          return;
      }

      try {
          loading = true;
          error = '';

          const result = await client.createPullRequest({
              token,
              ownerId,
              repositoryName,
              title: title.trim(),
              description: description.trim(),
              baseBranch,
              compareBranch,
              labelIds: [], // TODO: Map selectedLabels to label IDs
              reviewerIds: selectedReviewers,
              assigneeIds: selectedAssignees,
              milestoneId: '', // TODO: Map selectedMilestone
          });

          if (result.response.successFull) {
              dispatch('create', {
                  baseBranch,
                  compareBranch,
                  title,
                  description,
                  selectedReviewers,
                  selectedAssignees,
                  selectedLabels,
                  selectedMilestone
              });
          } else {
              error = result.response.reason || 'Failed to create pull request';
          }
      } catch (e: any) {
          error = e.message || 'Error creating pull request';
          console.error('Error creating pull request:', e);
      } finally {
          loading = false;
      } 
  }
</script>

<div class="bg-white min-h-screen text-gray-900 font-sans">
    <div class="max-w-6xl mx-auto p-6">
        <!-- Header -->
        <div class="mb-6 pb-4 border-b border-gray-200">
            <h1 class="text-2xl font-semibold text-gray-900">Create a new pull request</h1>
            <p class="text-sm text-gray-600 mt-1">Choose the branches to compare and create a pull request for the changes.</p>
        </div>

        <!-- Branch Selection -->
        <div class="mb-6 p-4 bg-gradient-to-r from-gray-50 to-blue-50 border border-blue-200 rounded-md flex flex-col md:flex-row items-center gap-4 text-sm shadow-sm">
            <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                <span class="font-medium text-gray-700">Compare changes</span>
            </div>
            
            <div class="flex items-center gap-2 flex-1">
                 <div class="relative">
                    <button
                        onclick={() => showBaseBranchDropdown = !showBaseBranchDropdown}
                        class="px-3 py-1.5 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 font-medium flex items-center gap-2"
                    >
                        <span class="text-gray-600">base:</span>
                        <span>{baseBranch}</span>
                        <svg class="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
                    </button>

                    {#if showBaseBranchDropdown}
                        <!-- Backdrop to dismiss dropdown -->
                        <div class="fixed inset-0 z-0" onclick={() => showBaseBranchDropdown = false}></div>

                        <!-- Dropdown menu -->
                        <div class="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
                            <div class="px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-200 bg-gray-50">
                                Choose a base branch
                            </div>
                            {#if loadingBranches}
                                <div class="px-3 py-4 text-sm text-gray-500 text-center">Loading branches...</div>
                            {:else if branches.length === 0}
                                <div class="px-3 py-4 text-sm text-gray-500 text-center">No branches found</div>
                            {:else}
                                {#each branches as branch}
                                    <button
                                        onclick={() => selectBaseBranch(branch)}
                                        class="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between border-b border-gray-100 last:border-0"
                                    >
                                        <span class="text-sm font-medium text-gray-700">{branch}</span>
                                        {#if branch === baseBranch}
                                            <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                            </svg>
                                        {/if}
                                    </button>
                                {/each}
                            {/if}
                        </div>
                    {/if}
                 </div>

                 <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>

                 <div class="relative">
                    <button
                        onclick={() => showCompareBranchDropdown = !showCompareBranchDropdown}
                        class="px-3 py-1.5 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 font-medium flex items-center gap-2"
                    >
                         <span class="text-gray-600">compare:</span>
                         <span>{compareBranch}</span>
                         <svg class="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
                    </button>

                    {#if showCompareBranchDropdown}
                        <!-- Backdrop to dismiss dropdown -->
                        <div class="fixed inset-0 z-0" onclick={() => showCompareBranchDropdown = false}></div>

                        <!-- Dropdown menu -->
                        <div class="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
                            <div class="px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-200 bg-gray-50">
                                Choose a compare branch
                            </div>
                            {#if loadingBranches}
                                <div class="px-3 py-4 text-sm text-gray-500 text-center">Loading branches...</div>
                            {:else if branches.length === 0}
                                <div class="px-3 py-4 text-sm text-gray-500 text-center">No branches found</div>
                            {:else}
                                {#each branches as branch}
                                    <button
                                        onclick={() => selectCompareBranch(branch)}
                                        class="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between border-b border-gray-100 last:border-0"
                                    >
                                        <span class="text-sm font-medium text-gray-700">{branch}</span>
                                        {#if branch === compareBranch}
                                            <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                            </svg>
                                        {/if}
                                    </button>
                                {/each}
                            {/if}
                        </div>
                    {/if}
                 </div>
            </div>
            
             <div class="text-green-600 text-xs font-medium flex items-center gap-1">
                 <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" /></svg>
                 Able to merge
             </div>
        </div>

        <div class="flex flex-col md:flex-row gap-8">
            
            <!-- Main Content (Left Column) -->
            <div class="flex-1">
                <div class="mb-6">
                    <label for="pr-title" class="block text-sm font-medium text-gray-700 mb-2">Title <span class="text-red-500">*</span></label>
                    <input
                        id="pr-title"
                        type="text"
                        bind:value={title}
                        placeholder="Brief description of the changes"
                        class="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-base focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                    />
                </div>

                <div class="mb-6">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <RichTextEditor bind:value={description} placeholder="Describe your pull request..." minHeight="350px" />
                </div>

                <div class="mt-6 pt-4 border-t border-gray-200 flex justify-end gap-3">
                     <button onclick={cancel} class="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md transition-all shadow-sm">
                        Cancel
                    </button>
                    <button onclick={submit} disabled={!title} class="px-5 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 border border-transparent rounded-md shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600">
                        Create pull request
                    </button>
                </div>
            </div>

            <!-- Sidebar (Right Column) -->
            <div class="w-full md:w-1/4">
                <div class="sticky top-6 space-y-6">
                    <div class="text-xs font-semibold text-gray-400 uppercase tracking-wide">Pull Request Options</div>

                <!-- Reviewers -->
                <div class="relative border-b border-gray-200 pb-4">
                    <div class="flex justify-between items-center mb-1 group cursor-pointer" onclick={() => showReviewers = !showReviewers}>
                        <h3 class="text-sm font-semibold text-gray-500 group-hover:text-blue-600">Reviewers</h3>
                        <svg class="w-4 h-4 text-gray-500 group-hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </div>
                    
                    {#if selectedReviewers.length === 0}
                         <div class="text-xs text-gray-500">No reviewers</div>
                    {:else}
                         <div class="space-y-1 mt-1">
                             {#each selectedReviewers as reviewerId}
                                {@const reviewer = members.find(m => m.userId === reviewerId)}
                                {@const displayUser = reviewer || (profile && reviewerId === profile.id ? { username: profile.userName, avatar: profile.avatarUrl, userId: profile.id, firstName: profile.firstName, lastName: profile.lastName } : null)}
                                {#if displayUser}
                                  <div class="flex items-center gap-1 text-sm text-gray-900 font-medium">
                                    {#if displayUser.avatar}
                                      <img src={displayUser.avatar} alt={displayUser.username} class="w-5 h-5 rounded-full" />
                                    {:else}
                                      <div class="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs">
                                        {((displayUser.firstName?.charAt(0) || '') + (displayUser.lastName?.charAt(0) || '')).toUpperCase() || 'U'}
                                      </div>
                                    {/if}
                                    {displayUser.username}
                                  </div>
                                {/if}
                             {/each}
                         </div>
                    {/if}

                    {#if showReviewers}
                        <div class="absolute top-8 left-0 w-full bg-white border border-gray-200 rounded-md shadow-lg z-10 text-sm max-h-64 overflow-y-auto">
                             <div class="px-3 py-2 border-b border-gray-100 font-medium text-gray-700 bg-gray-50 rounded-t-md">Request up to 15 reviewers</div>
                             {#if loadingMembers}
                               <div class="px-3 py-4 text-center text-gray-500">Loading members...</div>
                             {:else if members.length === 0}
                               <div class="px-3 py-4 text-center text-gray-500">No members found</div>
                             {:else}
                               {#each members as member}
                                   <button class="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 border-b border-gray-100 last:border-0" onclick={() => toggleReviewer(member.userId)}>
                                       {#if selectedReviewers.includes(member.userId)}
                                           <svg class="w-4 h-4 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                                       {:else}
                                           <div class="w-4 h-4"></div>
                                       {/if}
                                       {#if member.avatar}
                                         <img src={member.avatar} alt={member.username} class="w-5 h-5 rounded-full" />
                                       {:else}
                                         <div class="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs">
                                           {member.username.substring(0, 1).toUpperCase()}
                                         </div>
                                       {/if}
                                       <span class="font-medium text-gray-700">{member.username}</span>
                                   </button>
                               {/each}
                             {/if}
                        </div>
                        <div class="fixed inset-0 z-0" onclick={() => showReviewers = false}></div>
                    {/if}
                </div>

                <!-- Assignees -->
                <div class="relative border-b border-gray-200 pb-4">
                    <div class="flex justify-between items-center mb-1 group cursor-pointer" onclick={() => showAssignees = !showAssignees}>
                        <h3 class="text-sm font-semibold text-gray-500 group-hover:text-blue-600">Assignees</h3>
                        <svg class="w-4 h-4 text-gray-500 group-hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </div>
                    
                    {#if selectedAssignees.length === 0}
                         <div class="text-xs text-gray-500">No one—<button onclick={assignYourself} class="hover:text-blue-600">assign yourself</button></div>
                    {:else}
                         <div class="space-y-1 mt-1">
                             {#each selectedAssignees as assigneeId}
                                {@const assignee = members.find(m => m.userId === assigneeId)}
                                {@const displayUser = assignee || (profile && assigneeId === profile.id ? { username: profile.userName, avatar: profile.avatarUrl, userId: profile.id, firstName: profile.firstName, lastName: profile.lastName } : null)}
                                {#if displayUser}
                                  <div class="flex items-center gap-1 text-sm text-gray-900 font-medium">
                                    {#if displayUser.avatar}
                                      <img src={displayUser.avatar} alt={displayUser.username} class="w-5 h-5 rounded-full" />
                                    {:else}
                                      <div class="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs">
                                        {((displayUser.firstName?.charAt(0) || '') + (displayUser.lastName?.charAt(0) || '')).toUpperCase() || 'U'}
                                      </div>
                                    {/if}
                                    {displayUser.username}
                                  </div>
                                {/if}
                             {/each}
                         </div>
                    {/if}

                    {#if showAssignees}
                        <div class="absolute top-8 left-0 w-full bg-white border border-gray-200 rounded-md shadow-lg z-10 text-sm max-h-64 overflow-y-auto">
                             <div class="px-3 py-2 border-b border-gray-100 font-medium text-gray-700 bg-gray-50 rounded-t-md">Assign up to 10 people</div>
                             {#if loadingMembers}
                               <div class="px-3 py-4 text-center text-gray-500">Loading members...</div>
                             {:else if members.length === 0}
                               <div class="px-3 py-4 text-center text-gray-500">No members found</div>
                             {:else}
                               {#each members as member}
                                   <button class="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 border-b border-gray-100 last:border-0" onclick={() => toggleAssignee(member.userId)}>
                                       {#if selectedAssignees.includes(member.userId)}
                                           <svg class="w-4 h-4 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                                       {:else}
                                           <div class="w-4 h-4"></div>
                                       {/if}
                                       {#if member.avatar}
                                         <img src={member.avatar} alt={member.username} class="w-5 h-5 rounded-full" />
                                       {:else}
                                         <div class="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs">
                                           {member.username.substring(0, 1).toUpperCase()}
                                         </div>
                                       {/if}
                                       <span class="font-medium text-gray-700">{member.username}</span>
                                   </button>
                               {/each}
                             {/if}
                        </div>
                        <div class="fixed inset-0 z-0" onclick={() => showAssignees = false}></div>
                    {/if}
                </div>

                <!-- Labels -->
                <div class="relative border-b border-gray-200 pb-4">
                     <div class="flex justify-between items-center mb-1 group cursor-pointer" onclick={() => showLabels = !showLabels}>
                        <h3 class="text-sm font-semibold text-gray-500 group-hover:text-blue-600">Labels</h3>
                         <svg class="w-4 h-4 text-gray-500 group-hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                    </div>

                    {#if selectedLabels.length === 0}
                         <div class="text-xs text-gray-500">None yet</div>
                    {:else}
                         <div class="flex flex-wrap gap-1 mt-1">
                             {#each selectedLabels as labelName}
                                 {@const label = labels.find(l => l.name === labelName)}
                                 {#if label}
                                     <span 
                                        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" 
                                        style="background-color: {label.color}20; color: {label.color}; border: 1px solid {label.color}40;"
                                     >
                                        {label.name}
                                     </span>
                                 {/if}
                             {/each}
                         </div>
                    {/if}

                    {#if showLabels}
                        <div class="absolute top-8 left-0 w-full bg-white border border-gray-200 rounded-md shadow-lg z-10 text-sm">
                             <div class="px-3 py-2 border-b border-gray-100 font-medium text-gray-700 bg-gray-50 rounded-t-md">Apply labels to this PR</div>
                             <div class="max-h-60 overflow-y-auto">
                                 {#each labels as label}
                                     <button class="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-start gap-2 border-b border-gray-100 last:border-0" onclick={() => toggleLabel(label.name)}>
                                          {#if selectedLabels.includes(label.name)}
                                             <svg class="w-4 h-4 text-gray-900 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                                         {:else}
                                             <div class="w-4 h-4 flex-shrink-0"></div>
                                         {/if}
                                         <div>
                                             <div class="flex items-center gap-2">
                                                 <div class="w-3 h-3 rounded-full" style="background-color: {label.color}"></div>
                                                 <span class="font-medium text-gray-700">{label.name}</span>
                                             </div>
                                             <div class="text-xs text-gray-500 mt-0.5 pl-5">{label.description}</div>
                                         </div>
                                     </button>
                                 {/each}
                             </div>
                        </div>
                        <div class="fixed inset-0 z-0" onclick={() => showLabels = false}></div>
                    {/if}
                </div>

                <!-- Milestone -->
                 <div class="relative border-b border-gray-200 pb-4">
                     <div class="flex justify-between items-center mb-1 group cursor-pointer" onclick={() => showMilestones = !showMilestones}>
                        <h3 class="text-sm font-semibold text-gray-500 group-hover:text-blue-600">Milestone</h3>
                        <svg class="w-4 h-4 text-gray-500 group-hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 21v-8a2 2 0 012-2h14a2 2 0 012 2v8M3 21h18M5 10V7a4 4 0 014-4h6a4 4 0 014 4v3" />
                        </svg>
                    </div>

                    {#if !selectedMilestone}
                         <div class="text-xs text-gray-500">No milestone</div>
                    {:else}
                        <div class="mt-1">
                            <div class="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                <div class="h-full bg-green-500" style="width: 0%"></div>
                            </div>
                            <div class="text-sm font-medium text-gray-700 mt-1">{selectedMilestone}</div>
                        </div>
                    {/if}

                    {#if showMilestones}
                         <div class="absolute top-8 left-0 w-full bg-white border border-gray-200 rounded-md shadow-lg z-10 text-sm">
                             <div class="px-3 py-2 border-b border-gray-100 font-medium text-gray-700 bg-gray-50 rounded-t-md">Set milestone</div>
                             {#each milestones as milestone}
                                 <button class="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 border-b border-gray-100 last:border-0" onclick={() => selectMilestone(milestone.name)}>
                                     {#if selectedMilestone === milestone.name}
                                         <svg class="w-4 h-4 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                                     {:else}
                                         <div class="w-4 h-4"></div>
                                     {/if}
                                     <span class="font-medium text-gray-700">{milestone.name}</span>
                                 </button>
                             {/each}
                        </div>
                        <div class="fixed inset-0 z-0" onclick={() => showMilestones = false}></div>
                    {/if}
                </div>

                </div>
            </div>
        </div>
    </div>
</div>
